import React from "react";
import { FiMapPin } from "react-icons/fi";
import Logo from "../styles/Black-SVG-Landscape.svg";
import LihuePhoto from "../candidates/Lihue.png";

export default function ContentProducerRoleLihue() {
  const candidateProfileCopy = {
    name: "Lihué",
    location: "Mendoza, Argentina",
    compensation: {
      range: "$15 USD / hourly",
    },
    availability: "Available to start immediately",

    working_style:
      "Lihué is a strategy-first content operator who thinks in funnels and lead flows before she thinks in formats. She builds content with the end action in mind: what should the viewer do, and what is the fastest frictionless path to get them there. She is highly organized and data-aware, comfortable managing multi-platform calendars, email automations, and CRM-connected workflows simultaneously. She brings strong copywriting instincts — preferring manual refinement over raw AI output to keep content feeling human — and pairs this with a practical AI toolkit for research, image generation, and video production. She works well independently in remote, multicultural environments and brings a teacher's clarity to how she structures and explains content strategy.",

    loom_video_link: "https://fathom.video/share/bZ2gbJ4pRvmMwfBaEZcsRFWnoy5_MLH2",

    tools: [
      {
        category: "Content Strategy & Lead Generation",
        items: [
          "Lead magnet design and execution",
          "ManyChat keyword trigger automations",
          "Zapier integrations (ManyChat → CRM → email)",
          "UTM tracking and campaign attribution",
          "Email sequences and nurture workflows",
          "Funnel design (awareness → lead → conversion)",
        ],
      },
      {
        category: "Platforms & Publishing Ops",
        items: [
          "Instagram (organic + Reels + Stories)",
          "YouTube (video + SEO optimization)",
          "Meta Business Suite (scheduling, analytics, A/B testing)",
          "Kajabi (courses, funnels, email automations, launches)",
          "Wix (landing pages, basic SEO)",
          "GoDaddy (website updates)",
          "HubSpot (CRM, lead nurturing)",
          "Stripe (payments, subscriptions, checkout flows)",
        ],
      },
      {
        category: "Copywriting & SEO",
        items: [
          "Direct response copywriting",
          "Sales-focused social media copy",
          "Email marketing and newsletters",
          "SEO keyword integration into social and web copy",
          "Viral Reels scripting and hook writing",
          "Personal brand and positioning copy",
        ],
      },
      {
        category: "Video & Visual Production",
        items: [
          "CapCut (short-form video editing, Reels, TikToks, captions)",
          "Canva (graphics, templates, presentations, social media assets)",
          "Video and podcast repurposing (short-form + long-form)",
          "Bulk Instagram content production",
          "YouTube video editing with basic SEO optimization",
        ],
      },
      {
        category: "AI Tools & Workflow",
        items: [
          "ChatGPT (copywriting, research, content refinement)",
          "Gemini (comparative prompting, video generation support)",
          "Claude (copywriting and ideation)",
          "Ideogram / Nano (AI image generation, copyright-free assets)",
          "Multi-model prompting workflow (ChatGPT + Gemini + Claude comparison)",
        ],
      },
      {
        category: "Analytics & Marketing Operations",
        items: [
          "Meta Business Suite analytics (drop-off, engagement, saves)",
          "YouTube Studio analytics",
          "vidIQ (YouTube SEO and keyword research)",
          "UTM tracking and campaign performance reporting",
          "CRM updates and lead nurturing coordination",
          "Remote marketing operations (funnels, reporting, collaboration)",
        ],
      },
    ],

    soft_skills: [
      "Strong direct response and lead-gen copywriting instincts",
      "Systems thinker: builds content with funnel mechanics built in",
      "Highly organized across multi-platform, multi-client environments",
      "Data-aware: diagnoses content performance at the post and format level",
      "Human-voice editing: refines AI outputs to avoid generic copy",
      "Self-directed in remote, multicultural teams",
      "C1 Advanced English (St. Clare's, Oxford certified)",
      "Multilingual: English, German (B1), French, Portuguese, Latin",
    ],

    about_candidate:
      "Lihué is a digital marketing and social media specialist with 7+ years of experience in content strategy, lead generation, and marketing automation. She thinks in funnels: every piece of content she builds has a downstream lead capture action wired into it — lead magnet, ManyChat trigger, email sequence, CTA. She is a strong copywriter who manually refines all AI outputs to preserve human voice, and she manages the full publishing and distribution stack across Meta, YouTube, Kajabi, and HubSpot simultaneously.",

    core_strengths: [
      {
        headline: "Full-funnel lead generation built into every content piece",
        description:
          "Designs content so the lead capture mechanism is built in from the start: lead magnet → ManyChat keyword trigger → email capture → warm-up sequence → offer. Understands friction reduction at a tactical level and has executed this model across coaching, wellness, and e-commerce brands.",
      },
      {
        headline: "ManyChat + Zapier automation for lead capture at scale",
        description:
          "Has built and operated ManyChat automations connected to Zapier and external CRM platforms to capture leads directly from social media comments and DMs — reducing steps between content and contact.",
      },
      {
        headline: "SEO-integrated copywriting as a default practice",
        description:
          "Integrates SEO keywords into social copy and web content as a standard workflow step. Completed multiple dedicated YouTube growth and SEO training programs and applies search-aware writing across all platforms.",
      },
      {
        headline: "Performance diagnosis and content iteration",
        description:
          "Goes into platform statistics at the slide and frame level — carousel engagement by panel, save rates, drop-off points — and proposes format pivots, CTA adjustments, and cross-platform tests based on what the data shows.",
      },
    ],

    relevant_experience: [
      {
        headline: "Social Media Manager & Digital Executive | 2025–Present",
        bullets: [
          "Led content creation, copywriting, and scheduling across platforms for brand positioning, engagement, and lead generation",
          "Managed Kajabi funnels and email campaigns in collaboration with clients",
          "Executed marketing strategies tied to conversion and lead outcomes",
        ],
      },
      {
        headline: "Marketing Operations Assistant | Meet My Assistant | 2025–Present",
        bullets: [
          "Supported lead nurturing and conversion workflows through CRM updates, content coordination, and client communication",
        ],
      },
      {
        headline: "Social Media Manager | Grupo Slots Casino Online | 2025",
        bullets: [
          "Managed social media and lead-generation campaigns",
          "Executed bulk Instagram content, UTM tracking, and ManyChat automations",
          "Optimized performance while adhering to platform compliance standards",
        ],
      },
      {
        headline: "Project Manager | Advantaxe University | 2025",
        bullets: [
          "Managed Kajabi courses, funnels, email automations, and users",
          "Integrated Stripe payments and supported website updates (GoDaddy)",
          "Supported email campaigns, content uploads, and student onboarding",
          "Optimized and edited YouTube videos with basic SEO",
        ],
      },
      {
        headline: "Social Media Manager | Tammy Chow @somaticspirit | 2023–2025",
        bullets: [
          "Led digital marketing for online courses including Kajabi funnels and email automations",
          "Created high-converting social content and supported product launches",
          "Managed Wix landing pages and basic SEO",
        ],
      },
      {
        headline: "Social Media Manager | Depormanía · Aesthetic Fitness · Hotel Internacional & More | 2018–2025",
        bullets: [
          "Developed and executed multi-platform social media strategies to drive engagement, brand awareness, and sales",
          "Led campaigns for key commercial dates, promotions, and influencer collaborations",
          "Wrote sales-focused copy for social media, ads, and promotional materials",
          "Engaged with prospects and influencers to increase reach and conversions",
        ],
      },
      {
        headline: "Freelance Kajabi Exporter | Virtustant | Oct–Dec 2025",
        bullets: [
          "Exported and optimized Kajabi courses, email sequences, and digital assets",
          "Supported platform transitions while ensuring data accuracy and funnel integrity",
        ],
      },
    ],
  };

  // ─── PAGE 2 DATA ─────────────────────────────────────────────────────────────
  const technicalAssessment = {
    meta: {
      role: "Content Producer — Handy Manny's Remodeling",
      interviewDate: "March 06, 2026",
      analyst: "AI-Architechs Senior Technical Hiring Analysis",
    },

    snapshot:
      "Lihué is a well-rounded digital marketing and content operations professional whose strength lies in the strategic layer of content: lead funnels, lead magnets, CRM-connected workflows, copywriting, and platform-level performance diagnosis. She understands how content connects to business outcomes — not just aesthetics — and demonstrates clear direct-response instincts that align with Manny's \"content is a lead generation machine\" mandate. Her risk is on the production side: her video editing experience is CapCut and Canva-level, not cinematic post-production, which means the visual quality ceiling for short-form video is lower than the role may eventually demand. She is a strong choice if you need someone who can independently strategize, write, and distribute content tied to lead outcomes; she is a moderate risk if polished, high-production-value short-form video is the immediate top priority.",

    scorecard: [
      {
        dimension: "AI & Content Tools",
        score: 7,
        rationale:
          "Uses ChatGPT, Gemini, and Claude daily in a deliberate, comparative workflow — running the same prompt across all three to triangulate outputs and then manually editing for human voice. Uses AI for image generation (Nano), video generation (Gemini + CapCut + Canva), and research. Thoughtful about AI's limitations in creative ideation (\"it gives everyone the same ideas\"). Practical and grounded, but toolchain is lighter on dedicated video AI platforms compared to Daniel's stack.",
      },
      {
        dimension: "Core Platforms & Publishing Ops",
        score: 9,
        rationale:
          "Extensive hands-on experience across Meta, Instagram, YouTube, Kajabi, Wix, GoDaddy, ManyChat, Zapier, HubSpot, Canva, CapCut, Meta Ads, Google Ads, UTM tracking, and email marketing. Has managed multi-platform content calendars, email sequences, and funnels across 6+ roles over 7 years. This is one of her clearest strengths — she can operate across the full publishing and distribution stack, not just one platform.",
      },
      {
        dimension: "Performance & Lead Gen Mindset",
        score: 9,
        rationale:
          "Demonstrates the strongest lead-gen mindset of any candidate in this pool. Unprompted, walks through a full funnel: lead magnet → ManyChat keyword trigger → email capture → warm-up sequence → offer. References UTM tracking, ManyChat automations, and CRM-connected workflows from real work experience. Explicitly understands friction reduction in lead capture (\"the less steps the client has to make\"). This maps directly to Manny's need for content that drives inbound project inquiries.",
      },
      {
        dimension: "Short-Form Video Production & Editing",
        score: 6,
        rationale:
          "Competent with CapCut and Canva for short-form video, reels, and TikTok-style content. Has managed bulk Instagram content and YouTube video optimization. However, no evidence of film-level post-production skills (color grading, compositing, audio finishing, cinematic editing). Her video output is likely social-native and functional rather than cinematic. Adequate for consistent high-volume short-form posting; less suited for high-production-value showcase pieces.",
      },
      {
        dimension: "Storytelling, Hooks & Audience Psychology",
        score: 8,
        rationale:
          "Describes the \"tease and withhold\" formula clearly (carousel that saves the best item for a CTA at the end), references authentic brand alignment alongside hooks, and demonstrates understanding of attention spans (1–3 seconds). Goes beyond hooks to discuss emotional resonance, community storytelling, and behind-the-scenes content for engagement vs. lead-gen. Copywriting background adds depth to her messaging instincts.",
      },
      {
        dimension: "Search Intent & Topic Research",
        score: 7,
        rationale:
          "References using AI tools for SEO keyword research integrated directly into copywriting (\"add emojis and give me SEO keywords to work into this post so when people look it up they are highlighted\"). Has taken multiple dedicated YouTube SEO and growth courses (vidIQ, CreceTube, AutomatizaTube). Managed basic SEO on Wix landing pages. Does not reference advanced tools like SEMrush or Ubersuggest explicitly, but clearly practices search-aware content writing as a daily habit.",
      },
      {
        dimension: "Systemic Thinking & Content Engine Design",
        score: 8,
        rationale:
          "Describes end-to-end content workflows: lead magnet → social content → ManyChat trigger → email sequence → warm-up → offer. Has operated Kajabi funnels, email automations, and content pipelines across multiple clients simultaneously. CV shows experience with ManyChat + Zapier integration for lead capture. Can likely adapt quickly to the blueprint's \"raw project inputs → structured content → blog + social derivatives\" assembly line.",
      },
      {
        dimension: "Communication, Ownership & Collaboration",
        score: 9,
        rationale:
          "Communicates with exceptional clarity, fluency, and strategic depth in English. Gives structured, multi-layered answers without prompting. Shows independent thinking (critiques AI's creative limitations, proposes specific tactical formulas). Demonstrates a proactive, ownership mindset — proposes solutions rather than waiting for direction. C1 Advanced English certified (St. Clare's, Oxford). This is a meaningful differentiator for a role requiring clear async communication with Manny.",
      },
      {
        dimension: "Time Zone & Overlap Fit",
        score: 7,
        rationale:
          "Based in Mendoza, Argentina — UTC-3 year-round. This places her 2 hours ahead of U.S. Eastern in standard time and 1 hour ahead during U.S. daylight saving time. Morning overlap with Manny's 8am–8pm ET window is strong; she would be working from approximately 10am–10pm her local time to align. Workable for full-time remote collaboration, though the overlap is slightly less seamless than a Colombia-based candidate.",
      },
    ],

    strengths: [
      {
        headline: "Full-funnel lead generation thinking built into her content approach",
        description:
          "Lihué does not just create content — she wires it to lead capture. Her ManyChat + keyword trigger + email sequence workflow is exactly the kind of thinking Manny needs: every piece of content becomes a mechanism to collect contact information and start a conversation with a potential homeowner or developer. This maps directly to the client's core mandate that \"content is a lead generation machine, not a brand exercise.\"",
      },
      {
        headline: "Platform and tools breadth that covers the full content operations stack",
        description:
          "Across Kajabi, Meta, YouTube, ManyChat, Zapier, HubSpot, Wix, CapCut, Canva, UTM tracking, and email marketing, Lihué can operate across the entire publishing and distribution infrastructure without needing additional tooling support. This means she can own the content calendar, the scheduling, the analytics, the lead capture mechanics, and the email follow-up layer — reducing the coordination burden on Manny and the AI Architect.",
      },
      {
        headline: "SEO-aware copywriting as a daily practice",
        description:
          "She integrates SEO keywords into social copy as a default workflow habit, not an afterthought, and has completed multiple dedicated YouTube SEO and growth training programs. This directly supports the blueprint's goal of building search visibility and AI discoverability for Manny's remodeling business, where long-tail local queries are a key traffic driver.",
      },
      {
        headline: "Strong creative judgment about AI's limitations",
        description:
          "She explicitly warns against using ChatGPT for content ideation (\"it gives everyone the same ideas, they are going to feel very unreal\") and insists on manually preserving originality and human voice in all copy. This is a mature, client-protecting instinct that ensures Manny's content will feel authentic to real projects and real people — not templated AI output.",
      },
      {
        headline: "Performance diagnosis and iteration mindset",
        description:
          "When content underperforms, she goes directly into platform statistics: carousel slide-level engagement, save rates, drop-off points. Then she proposes format pivots (carousel → video), CTA adjustments, and cross-platform testing (feed → stories → LinkedIn). This data-informed iteration loop is exactly what a content engine needs to improve over time and keep generating qualified leads.",
      },
    ],

    flags: [
      {
        headline: "Video production quality ceiling is lower than the role may eventually require",
        description:
          "Her video toolkit is CapCut and Canva — capable for social-native short-form content but not for cinematic, high-production-value showcase videos of $100K–$1M remodeling projects. For early-stage consistent content volume, this is entirely adequate. But if Manny's vision evolves toward premium before/after showcase videos or high-end property walkthroughs, her production ceiling may become a constraint. Manageable at current scope; worth monitoring as the content engine matures.",
      },
      {
        headline: "No direct construction or home services content experience",
        description:
          "Her portfolio spans online education, e-commerce, wellness, and online gaming. Remodeling-specific content dynamics — trust signals for large financial commitments, proof of scope, speaking to developers and architects — are not evidenced. She will need early positioning briefs and domain context from Manny. Manageable and expected; her strong strategic instincts will accelerate the ramp.",
      },
      {
        headline: "AI video generation toolchain is lighter",
        description:
          "While she uses Gemini and CapCut for video generation, she does not describe the depth of dedicated AI video platforms (Runway, Kling, Midjourney for image-to-video, Suno for music scoring) that could elevate output quality significantly. For high-volume social content this may not matter day-to-day, but it is worth noting if cinematic AI-augmented content becomes a priority.",
      },
    ],

    when_to_hire: [
      "You need someone who can independently own the strategy, copywriting, lead capture mechanics, and content distribution layer — and who will connect content to lead outcomes without being told to.",
      "You prioritize consistent, high-volume, strategically sound content over cinematically polished video production, especially in the first 90 days of building the content engine.",
      "The AI Architect is focused on technical infrastructure (automations, attribution, dashboards) and you need the Content Producer to bring strong marketing brain + operational execution, not just creative production.",
      "You want a hire who will proactively propose experiments, CTAs, and funnel mechanics rather than waiting for direction on what to post and why.",
      "Strong English communication and async collaboration fluency are important for working directly with Manny and the broader team.",
    ],

    when_to_be_cautious: [
      "Cinematic, high-production-value short-form video — showcasing large remodeling projects with polished editing, color grading, and sound design — is your immediate top priority from day one.",
      "You need a creator with deep AI video generation workflows (Runway, Kling, Midjourney, Suno) to produce visually elevated content at scale.",
      "You want the content to feel visually premium enough to attract $500K–$1M commercial build-out clients who may judge production quality as a proxy for business quality.",
    ],

    bottom_line:
      "Lihué is a strategically sharp, operationally capable content professional who brings the strongest lead-generation mindset and platform operations depth in this candidate pool. Her gap is production quality on video, not strategic or operational capability. As a Content Producer in the role as scoped — especially in the first phase of building a consistent content engine — she is a strong candidate and a recommended hire, with the note that video production quality should be monitored and supplemented if the role evolves toward premium showcase content.",
  };

  // Score pill helper
  const ScorePill = ({ score }) => {
    let bg = "#f97316";
    if (score >= 8) bg = "#22c55e";
    else if (score >= 6) bg = "#36D1FF";
    return (
      <span
        style={{ backgroundColor: bg }}
        className="inline-block text-white text-xs font-bold px-3 py-1 rounded-full"
      >
        {score} / 10
      </span>
    );
  };

  const weightedAvg = (
    technicalAssessment.scorecard.reduce((sum, r) => sum + r.score, 0) /
    technicalAssessment.scorecard.length
  ).toFixed(1);

  return (
    <div className="min-h-screen bg-[#01014C] text-white flex flex-col items-center py-10 px-4">

      {/* ===================== PAGE 1 ===================== */}
      <div className="w-full max-w-6xl bg-[#FFFDFB] rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row relative">

        {/* LEFT COLUMN */}
        <aside className="relative md:w-1/3 bg-gradient-to-b from-[#01014C] via-[#0A0A80] to-[#36D1FF] p-8 flex flex-col items-center text-center overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-[#36D1FF]/30 to-transparent"></div>

          {/* Avatar */}
          <div className="relative mb-4">
            <div className="absolute inset-0 rounded-full bg-[#36D1FF]/20 blur-2xl"></div>
            <img
              src={LihuePhoto}
              alt={candidateProfileCopy.name}
              className="relative w-36 h-36 rounded-full object-cover border-4 border-[#36D1FF] shadow-lg"
            />
          </div>

          {/* Name */}
          <h1 className="text-2xl font-semibold mb-1 text-[#FFFDFB]">
            {candidateProfileCopy.name}
          </h1>

          {/* Location */}
          <div className="flex items-center gap-2 text-[#E0F7FF] text-sm mb-6 opacity-90">
            <FiMapPin className="text-[#36D1FF]" />
            <span>{candidateProfileCopy.location}</span>
          </div>

          {/* Compensation */}
          <div className="bg-[#01014C] border border-[#36D1FF]/50 rounded-xl p-4 w-full text-left mb-4">
            <p className="text-[#B3E9FF] text-sm font-semibold uppercase mb-1">
              Compensation
            </p>
            <p className="text-[#36D1FF] text-lg font-bold">
              {candidateProfileCopy.compensation.range}
            </p>
          </div>

          {/* Availability */}
          <div className="bg-[#01014C] border border-[#36D1FF]/30 rounded-xl p-4 w-full text-left mb-6">
            <p className="text-[#E0F7FF] text-sm">
              {candidateProfileCopy.availability}
            </p>
          </div>

          {/* Video Intro */}
          {candidateProfileCopy.loom_video_link && (
            <div className="w-full mt-4">
              <a
                href={candidateProfileCopy.loom_video_link}
                target="_blank"
                rel="noreferrer"
                className="block w-full text-center font-semibold py-3 rounded-lg
                           bg-[#36D1FF] text-[#01014C]
                           hover:bg-[#5FE2FF]
                           transition-all duration-300"
              >
                🎥 Watch Interview
              </a>
            </div>
          )}

          {/* Working Style */}
          <div className="bg-[#36D1FF]/5 border border-[#36D1FF]/20 p-4 rounded-xl text-sm text-[#E0F7FF] mb-6 mt-6">
            {candidateProfileCopy.working_style}
          </div>

          {/* Tools */}
          <div className="w-full mb-6 text-left">
            <h3 className="text-sm font-semibold mb-2 text-[#B3E9FF] uppercase">
              Tools
            </h3>
            {candidateProfileCopy.tools.map((group, i) => (
              <div key={i} className="mb-3">
                <p className="text-xs text-[#E0F7FF]/80 mb-1">{group.category}</p>
                <div className="flex flex-wrap gap-2">
                  {group.items.map((tool, j) => (
                    <span
                      key={j}
                      className="bg-[#36D1FF]/10 text-[#36D1FF] text-xs px-2 py-1 rounded-full border border-[#36D1FF]/30"
                    >
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Soft Skills */}
          <div className="w-full mb-6 text-left">
            <h3 className="text-sm font-semibold mb-2 text-[#B3E9FF] uppercase">
              Soft Skills
            </h3>
            <ul className="list-disc pl-5 space-y-1 text-[#E0F7FF] text-sm">
              {candidateProfileCopy.soft_skills.map((skill, i) => (
                <li key={i}>{skill}</li>
              ))}
            </ul>
          </div>

          {/* Logo */}
          <div className="w-full mt-8 flex justify-center">
            <img src={Logo} alt="AI-Architechs Logo" className="w-42 opacity-80" />
          </div>
        </aside>

        {/* RIGHT COLUMN */}
        <main className="flex-1 p-10 text-[#01014C] space-y-10">
          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              About this Candidate
            </h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
              {candidateProfileCopy.about_candidate}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              Core Strengths
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {candidateProfileCopy.core_strengths.map((item, i) => (
                <div key={i} className="border border-[#36D1FF]/30 rounded-xl p-5">
                  <p className="font-semibold mb-1">{item.headline}</p>
                  <p className="text-sm text-gray-700">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              Relevant Experience
            </h2>
            <div className="space-y-6">
              {candidateProfileCopy.relevant_experience.map((exp, i) => (
                <div key={i} className="border border-[#36D1FF]/30 rounded-xl p-5">
                  <p className="font-semibold text-[#EB5D2A] mb-2">{exp.headline}</p>
                  <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700">
                    {exp.bullets.map((b, j) => (
                      <li key={j}>{b}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </main>
      </div>

      {/* ===================== PAGE BREAK BAND ===================== */}
      <div className="w-full py-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="h-10 rounded-2xl bg-gradient-to-r from-[#01014C] via-[#0A0A80] to-[#36D1FF] opacity-70 shadow-[0_0_40px_#36D1FF20]" />
        </div>
      </div>

      {/* ===================== PAGE 2 ===================== */}
      <div className="w-full max-w-6xl bg-[#FFFDFB] rounded-3xl overflow-hidden shadow-2xl pb-2">
        <main className="p-10 text-[#01014C]">
          <div className="border border-[#36D1FF]/25 rounded-2xl p-6 bg-white shadow-[0_0_12px_#36D1FF15]">

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mb-6">
              <h2 className="text-2xl font-semibold text-[#01014C]">
                Technical Analysis
              </h2>
              <div className="flex flex-col items-start md:items-end gap-1 text-xs text-gray-400">
                <span>{technicalAssessment.meta.role}</span>
                <span>Interview: {technicalAssessment.meta.interviewDate}</span>
                <span>{technicalAssessment.meta.analyst}</span>
              </div>
            </div>

            {/* Snapshot */}
            <div className="border border-[#36D1FF]/20 rounded-xl p-5 bg-[#36D1FF]/5 mb-6">
              <p className="text-base font-bold text-[#01014C] mb-2">Executive Fit Snapshot</p>
              <p className="text-gray-700 text-base leading-relaxed whitespace-pre-line">
                {technicalAssessment.snapshot}
              </p>
            </div>

            {/* Strengths & Flags */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">

              {/* Strengths */}
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ✅ Strengths
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.strengths.map((item, i) => (
                    <div
                      key={i}
                      className="border border-[#22c55e]/30 rounded-xl p-4 bg-[#22c55e]/5"
                    >
                      <p className="font-semibold text-sm text-[#01014C] mb-1">
                        {item.headline}
                      </p>
                      <p className="text-sm text-gray-700">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Flags */}
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ⚠️ Flags / Watch-outs
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.flags.map((item, i) => (
                    <div
                      key={i}
                      className="border border-[#f97316]/30 rounded-xl p-4 bg-[#f97316]/5"
                    >
                      <p className="font-semibold text-sm text-[#01014C] mb-1">
                        {item.headline}
                      </p>
                      <p className="text-sm text-gray-700">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Scorecard */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                Quantitative Scorecard
              </h3>
              <div className="overflow-x-auto rounded-xl border border-[#36D1FF]/25">
                <table className="w-full text-sm">
                  <thead className="bg-[#36D1FF]/10 text-[#01014C]">
                    <tr>
                      <th className="text-left font-semibold px-4 py-3">Dimension</th>
                      <th className="text-left font-semibold px-4 py-3 w-[120px]">Score</th>
                      <th className="text-left font-semibold px-4 py-3">Rationale</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#36D1FF]/15">
                    {technicalAssessment.scorecard.map((row, idx) => (
                      <tr key={idx} className="bg-white">
                        <td className="px-4 py-3 font-medium text-[#01014C] align-top">
                          {row.dimension}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <ScorePill score={row.score} />
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-pre-line align-top">
                          {row.rationale}
                        </td>
                      </tr>
                    ))}
                    {/* Weighted Average Row */}
                    <tr className="bg-[#01014C]/5 font-semibold">
                      <td className="px-4 py-3 text-[#01014C]">Weighted Average</td>
                      <td className="px-4 py-3">
                        <ScorePill score={parseFloat(weightedAvg)} />
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm italic">
                        Average across all 9 dimensions
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* When to Hire / Considerations */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">

              {/* When to Hire */}
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ✅ Hire Lihué if…
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.when_to_hire.map((item, i) => (
                    <div
                      key={i}
                      className="border border-[#36D1FF]/30 rounded-xl p-4 bg-[#36D1FF]/5 text-sm text-gray-700"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* When to Be Cautious */}
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ⚠️ Be cautious or pass if…
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.when_to_be_cautious.map((item, i) => (
                    <div
                      key={i}
                      className="border border-[#f59e0b]/30 rounded-xl p-4 bg-[#f59e0b]/5 text-sm text-gray-700"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom Line */}
            <div className="border border-[#01014C]/20 rounded-xl p-5 bg-[#01014C]/5">
              <p className="text-sm font-bold text-[#01014C] mb-2 uppercase tracking-wide">
                🏁 Bottom Line
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                {technicalAssessment.bottom_line}
              </p>
            </div>

          </div>
        </main>
      </div>

    </div>
  );
}
